import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { RouterProvider } from "react-router-dom";

import * as Sentry from "@sentry/react";

import store from "./store";

import "./styles/index.css";

import { logger } from "./helpers/logger";
import {
  Configuration,
  ConfigurationContext,
} from "./providers/ConfigurationContext.tsx";
import router from "./router";

const initializeApplication = async (element: HTMLElement) => {
  const environment =
    (element.getAttribute("data-environment") as string) ||
    import.meta.env.MODE;

  if (import.meta.env.VITE_SENTRY_ENABLED === "1") {
    Sentry.init({
      dsn: "https://2e6041629435c6371595bc92f133a748@o1132036.ingest.us.sentry.io/4509306978041856",
      environment,
      sendDefaultPii: true,
      release: __APP_VERSION__,
      sampleRate: 1.0,
    });
  }

  logger.debug("Initializing application", {
    key: "environment",
    value: environment,
  });
};

const mountApplication = async (element: HTMLElement) => {
  const configuration: Configuration = {};

  if (element.dataset.model) {
    configuration.model = parseInt(element.dataset.model, 10);
  }
  if (element.dataset.series) {
    configuration.series = parseInt(element.dataset.series, 10);
  }
  if (element.dataset.variant) {
    configuration.variant = parseInt(element.dataset.variant, 10);
  }
  if (element.dataset.orderId) {
    configuration.orderId = parseInt(element.dataset.orderId, 10);
  }
  if (element.dataset.wcStoreNonce) {
    configuration.wcStoreNonce = element.dataset.wcStoreNonce;
  }

  return initializeApplication(element).then(() => {
    createRoot(element).render(
      <StrictMode>
        <ConfigurationContext value={configuration}>
          <Provider store={store}>
            <RouterProvider router={router} />
          </Provider>
        </ConfigurationContext>
      </StrictMode>,
    );
  });
};

const app = document.getElementById("gvm-builder");
if (!app) {
  throw new Error("Root element not found");
}

mountApplication(app).catch((error) => {
  logger.error("Failed to mount application:", error);
});
